import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
  useImperativeHandle,
} from "react";
import { Container, Graphics } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import { POPUP_OVERLAY_CONFIG } from "../../../pixiSettings";


import anime from "animejs/lib/anime.es.js";
import WinAnimationSecondaryController from "./WinAnimationSecondaryController";
import {
  selectWinAnimationsPlaying,
  selectBet,
  selectCurrentRound,
  selectFreeSpinActive,
  selectFreeSpinsActive,
  selectFreeSpinsCurrentRound,
} from "../../../State/selectors";
import { setWinAnimationsPlaying } from "../../../State/actions";
import { attemptFullscreenToggle } from "../../../utilities";
// Win celebration backdrop — the one shared overlay, same as every popup.
const WIN_OVERLAY_COLOR = POPUP_OVERLAY_CONFIG.COLOR;
const WIN_OVERLAY_ALPHA = POPUP_OVERLAY_CONFIG.ALPHA;



const WinAnimationSecondary = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const winControllerRef = useRef(null);
  const winContainerRef = useRef(null);
  const overlayRef = useRef(null);
  const handleUserDismissRef = useRef(null);
  const dimensionsContext = useDimensions();
  const {
    dimensions,
    uiSymbolHeight: uiSymbolSize,
    device,
  } = dimensionsContext;


  // Overlay state
  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const overlayAnimRef = useRef(null);

  // Expose fadeInBackground / fadeOutBackground via overlayRef (same API as Overlay component)
  useImperativeHandle(overlayRef, () => ({
    fadeInBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: 0 },
        alpha: WIN_OVERLAY_ALPHA,
        duration: POPUP_OVERLAY_CONFIG.FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          if (callback && typeof callback === "function") callback();
        },
      });
    },
    fadeOutBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: WIN_OVERLAY_ALPHA },
        alpha: 0,
        duration: POPUP_OVERLAY_CONFIG.FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          setOverlayAlpha(0);
          if (callback && typeof callback === "function") callback();
        },
      });
    },
    setAlpha(alpha) {
      setOverlayAlpha(alpha);
    },
  }));

  const { winAnimationControllerRef } = useReelRefs();

  // Get win animations playing state and actual game data
  const winAnimationsPlaying = useSelector(selectWinAnimationsPlaying);
  const bet = useSelector(selectBet);
  const currentRound = useSelector(selectCurrentRound);
  const freeSpinActive = useSelector(selectFreeSpinActive);

  // ── Which round is celebrating ──
  //
  // WinAnimationSecondaryController.getWinSequence already makes exactly this
  // switch, and this component was left reading the base round only. During
  // free spins game.currentRound is the trigger spin, frozen — and on a bonus
  // BUY it paid 0. So `winAmount > 0` was false, startSequence never ran, and
  // because the only setWinAnimationsPlaying(false) lives in the finish path
  // of a sequence that never started, the flag stayed true for ever.
  //
  // That is a dead round, not a missing animation: the free-spin loop gates
  // the next spin on that flag, WinText hides itself while it is set, and
  // WinDisplay's win row is suppressed with it. One un-switched selector
  // stopped the whole feature.
  const freeSpinsActive = useSelector(selectFreeSpinsActive);
  const freeSpinsRound = useSelector(selectFreeSpinsCurrentRound);
  const activeRound = freeSpinsActive ? freeSpinsRound : currentRound;

  // Use win amount from the round that is actually on screen.
  const winAmount = activeRound?.win || 0;

  // Initialize WinAnimationController when assets are ready
  useEffect(() => {
    if (winContainerRef.current && !winControllerRef.current) {
      winControllerRef.current = new WinAnimationSecondaryController(
        winContainerRef.current,
        dimensionsContext,
        overlayRef,
        dispatch,
        uiSymbolSize,
        uiSymbolSize,
        t,
      );
      // "TAP TO CONTINUE" text routes through the same dismiss flow (fullscreen
      // + sound + skip/close) as the full-screen overlay. Proxy via ref so it
      // always calls the latest handleUserDismiss, not a stale mount-time closure.
      winControllerRef.current.setClickHandler((...args) => {
        if (handleUserDismissRef.current) handleUserDismissRef.current(...args);
      });

      // Shared so the dev panel can force a tier sequence. The game path does
      // not use it — the real sequence still starts from the effect below.
      if (winAnimationControllerRef) {
        winAnimationControllerRef.current = winControllerRef.current;
      }
    }

    return () => {
      if (winAnimationControllerRef) winAnimationControllerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (winControllerRef.current && uiSymbolSize && dimensionsContext) {
      winControllerRef.current.updateDimensions(
        dimensionsContext,
        uiSymbolSize,
        uiSymbolSize,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions.width, dimensions.height, uiSymbolSize, device]);

  // Handle user click: fullscreen (if needed) + continue animation
  const handleUserDismiss = useCallback(
    async (evt) => {
      if (evt && typeof evt.preventDefault === "function") evt.preventDefault();
      if (evt && typeof evt.stopPropagation === "function")
        evt.stopPropagation();
      if (evt && typeof evt.stopImmediatePropagation === "function")
        evt.stopImmediatePropagation();

      await attemptFullscreenToggle(dispatch, device);

      if (winControllerRef.current && winAnimationsPlaying) {
        // No sound here any more. A tap is accepted twice on this screen —
        // once to jump the counter, once to close — and only the close makes a
        // sound now. It is played by the controller's playClosingAnimation,
        // which is also where the auto-close timer ends up, so a celebration
        // that times out closes with the same sound as one that is tapped.
        winControllerRef.current.handleUserClick();
      }
    },
    [winAnimationsPlaying, dispatch, device],
  );

  // Keep ref current so the controller's proxy always calls the latest version
  handleUserDismissRef.current = handleUserDismiss;

  // Add spacebar handling - ensure this takes priority over other handlers
  useEffect(() => {
    const handleKeyDown = async (event) => {
      // Only handle spacebar
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }

      // Ignore auto-repeat (holding the key)
      if (event.repeat) {
        event.preventDefault();
        if (typeof event.stopPropagation === "function")
          event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function")
          event.stopImmediatePropagation();
        return;
      }

      // Only handle when animations are playing AND controller exists
      if (!winAnimationsPlaying || !winControllerRef.current) {
        return;
      }

      // Prevent event from reaching other handlers
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      // Emergency recovery check
      try {
        await handleUserDismiss(event);
      } catch (error) {
        // Emergency recovery - try to finish animation
        if (
          winControllerRef.current &&
          winControllerRef.current.finishAnimation
        ) {
          winControllerRef.current.finishAnimation();
        }
      }
    };

    // Use capture phase to ensure we get the event first
    if (winAnimationsPlaying) {
      document.addEventListener("keydown", handleKeyDown, {
        capture: true,
        passive: false, // Allow preventDefault
      });
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [winAnimationsPlaying, handleUserDismiss]);

  // Start animation when winAnimationsPlaying becomes true and we have a valid win
  useEffect(() => {
    if (winAnimationsPlaying && winControllerRef.current && winAmount > 0) {
      // Use the bet stored inside the round (the bet that actually produced this win).
      // Falls back to current Redux bet for live spins where round.bet may not be set yet.
      const roundBet = activeRound?.bet || bet;
      winControllerRef.current.startSequence(winAmount, roundBet);
    } else if (winAnimationsPlaying && winControllerRef.current) {
      // ── The flag is up and there is nothing to play ──
      //
      // Whoever raised it is waiting for the finish path to lower it, and the
      // finish path only runs at the end of a sequence that is not going to
      // start. Lower it here instead. This is the deadlock above, made
      // impossible rather than merely fixed: any future round that raises the
      // flag with a zero win — or with a win type this spine has no sequence
      // for — now resolves instead of stopping the game.
      dispatch(setWinAnimationsPlaying(false));
    } else if (!winAnimationsPlaying && winControllerRef.current) {
      // Stop animation when state becomes false
      winControllerRef.current.stopWinAnimation();
    }
  }, [
    winAnimationsPlaying,
    winAmount,
    bet,
    freeSpinActive,
    activeRound,
    dispatch,
  ]);

  // Cleanup overlay animation on unmount
  useEffect(() => {
    return () => {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
    };
  }, []);

  // Memoize container position to prevent recalculation on every render
  const containerPosition = useMemo(
    () => ({
      x: dimensions.width / 2,
      y: dimensions.height / 2,
    }),
    [dimensions.width, dimensions.height],
  );

  // Overlay draw function (WarningPopup style)
  const drawOverlay = useCallback(
    (g) => {
      g.clear();
      g.beginFill(WIN_OVERLAY_COLOR);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height],
  );

  return (
    <Container
      name="win_animation"
      x={containerPosition.x}
      y={containerPosition.y}
      sortableChildren={true}
      zIndex={10}
      eventMode={winAnimationsPlaying ? "auto" : "none"}
      ref={winContainerRef}
    >
      {/* Background overlay - inline Graphics (WarningPopup pattern) */}
      <Graphics
        name="win_overlay"
        draw={drawOverlay}
        alpha={overlayAlpha}
        x={-containerPosition.x}
        y={-containerPosition.y}
        zIndex={0}
        eventMode={winAnimationsPlaying ? "static" : "none"}
        pointerdown={handleUserDismiss}
        cursor="default"
      />

      {/* Win spine, counter, and button are managed internally by WinAnimationSecondaryController */}
    </Container>
  );
};

export default WinAnimationSecondary;
